import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { StaffSchedule } from "@/types/staff-schedule";
import { useUpdateStaffSchedule } from "@/hooks/useStaffSchedule";
import { ScheduleForm, StaffOption } from "./ScheduleForm";
import { ScheduleFormData } from "../utils/scheduleSchema";
import { getHttpStatus, getUserFriendlyErrorMessage } from "@/utils/httpErrors";

interface EditScheduleDialogProps {
  schedule: StaffSchedule | null;
  hotelId: string;
  staffOptions: StaffOption[];
  onClose: () => void;
}

const normalizeTime = (value: string): string => {
  const timePart = value.includes("T") ? (value.split("T")[1] ?? value) : value;
  return timePart.slice(0, 5);
};

const resolveEffectiveTo = (
  formValue: string | undefined,
  previousValue: string | null,
): string | null | undefined => {
  if (formValue) return formValue;
  return previousValue ? null : undefined;
};

export const EditScheduleDialog: React.FC<EditScheduleDialogProps> = ({
  schedule,
  hotelId,
  staffOptions,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mutate: updateSchedule, isPending } = useUpdateStaffSchedule();

  const initialValues: Partial<ScheduleFormData> | undefined = useMemo(() => {
    if (!schedule) return undefined;
    return {
      staffId: schedule.staffId,
      recurrenceType: schedule.recurrenceType,
      recurrenceInterval: schedule.recurrenceInterval ?? undefined,
      days: schedule.days,
      anchorDate: schedule.anchorDate.split("T")[0],
      startTime: normalizeTime(schedule.startTime),
      endTime: normalizeTime(schedule.endTime),
      effectiveFrom: schedule.effectiveFrom.split("T")[0],
      effectiveTo: schedule.effectiveTo?.split("T")[0] ?? "",
    };
  }, [schedule]);

  const handleSubmit = (data: ScheduleFormData) => {
    if (!schedule) return;

    updateSchedule(
      {
        id: schedule.id,
        hotelId,
        data: {
          recurrenceType: data.recurrenceType,
          recurrenceInterval: data.recurrenceInterval,
          days: data.days,
          anchorDate: data.anchorDate,
          startTime: data.startTime,
          endTime: data.endTime,
          effectiveFrom: data.effectiveFrom,
          effectiveTo: resolveEffectiveTo(
            data.effectiveTo,
            schedule.effectiveTo,
          ),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("staffSchedule.update.success"));
          onClose();
        },
        onError: (error) => {
          if (getHttpStatus(error) === 409) {
            toast.error(t("staffSchedule.update.conflict"));
          } else {
            toast.error(
              getUserFriendlyErrorMessage(
                error,
                t("staffSchedule.update.error"),
              ),
            );
          }
        },
      },
    );
  };

  return (
    <Dialog open={!!schedule} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("staffSchedule.page.editSchedule")}</DialogTitle>
        </DialogHeader>
        {initialValues && (
          <ScheduleForm
            staffOptions={staffOptions}
            onSubmit={handleSubmit}
            isSubmitting={isPending}
            initialValues={initialValues}
            isEdit
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
